import { Database, Tables } from "@punchup/data/supabase.types";
import { SupabaseClient } from "@supabase/supabase-js";
import { logException } from "@/analytics/sentry";

export type CommentReaction = Tables<"comment_reactions">;

export async function getTotalReactionCountForComment(
  supabase: SupabaseClient<Database>,
  commentId: string,
  type: CommentReaction["reaction_type"]
): Promise<number> {
  const { status, data, error } = await supabase
    .from("comment_reactions")
    .select("*")
    .eq("reaction_type", type)
    .eq("comment_id", commentId);

  if (error || !data) {
    logException(error, {
      tags: { commentId },
    });
    return 0;
  } else if (status === 200) {
    // Since comment_reactions doesn't have count field, count individual records
    return data.length;
  }

  throw new Error(`Error counting reactions for comment ID ${commentId}`);
}

export async function getUserReactionCountForComment(
  supabase: SupabaseClient<Database>,
  commentId: string,
  type: CommentReaction["reaction_type"]
): Promise<number> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.user) {
    logException(
      new Error(
        `No user session found when retrieving ${type} reactions for comment`
      ),
      { tags: { commentId } }
    );
    return 0;
  }

  const { status, data, error } = await supabase
    .from("comment_reactions")
    .select("*")
    .eq("reaction_type", type)
    .eq("user_id", session.user.id)
    .eq("comment_id", commentId);

  if (error || !data) {
    logException(error, {
      tags: { commentId, userId: session.user.id },
    });
    return 0;
  } else if (status === 200) {
    // Since comment_reactions doesn't have count field, count individual records
    return data.length;
  }

  throw new Error(
    `Error finding user reactions for comment ID ${commentId} and user ID ${session.user.id}`
  );
}

export async function getUserReactionsForComment(
  supabase: SupabaseClient<Database>,
  commentId: string,
  type: CommentReaction["reaction_type"]
): Promise<CommentReaction[]> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.user) {
    logException(
      new Error(
        `No user session found when retrieving ${type} reactions for comment`
      ),
      { tags: { commentId } }
    );
    return [];
  }

  const { status, data, error } = await supabase
    .from("comment_reactions")
    .select("*")
    .eq("reaction_type", type)
    .eq("user_id", session.user.id)
    .eq("comment_id", commentId);

  if (error) {
    logException(error);
    return [];
  } else if (status === 200) {
    return data;
  }

  throw new Error(
    `Error finding user reactions for comment ID ${commentId} and user ID ${session.user.id}`
  );
}

export async function createUserReactionForComment(
  supabase: SupabaseClient<Database>,
  commentId: string,
  type: CommentReaction["reaction_type"],
  count: number
): Promise<number> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.user) {
    logException(
      new Error(
        `No user session found when creating ${type} reactions for comment`
      ),
      {
        extra: {
          commentId,
          count,
        },
        tags: {
          commentId,
        },
      }
    );
    return 0;
  }

  // Check how many reactions this user already has for this comment
  const existingCount = await getUserReactionCountForComment(
    supabase,
    commentId,
    type
  );

  // Calculate how many new reactions we can actually add
  const maxNewReactions = Math.max(
    0,
    MAX_COMMENT_CLAP_REACTIONS - existingCount
  );
  const reactionsToAdd = Math.min(count, maxNewReactions);

  if (reactionsToAdd === 0) {
    console.log(`User already has ${existingCount} reactions, cannot add more`);
    return 0;
  }

  // For comments, we need to insert multiple individual records
  const reactions = [];
  for (let i = 0; i < reactionsToAdd; i++) {
    reactions.push({
      user_id: session.user.id,
      comment_id: commentId,
      reaction_type: type,
    });
  }

  const { status, data, error } = await supabase
    .from("comment_reactions")
    .insert(reactions)
    .select();

  if (error || !data) {
    logException(error, {
      extra: {
        commentId,
        count: reactionsToAdd,
        existingCount,
      },
      tags: { commentId, userId: session.user.id },
    });
    return 0;
  } else if (status === 201) {
    return data.length;
  }

  throw new Error(
    `Error adding user reaction type ${type} with count ${reactionsToAdd} for comment ID ${commentId} and user ID ${session.user.id}`
  );
}

export async function getCommentsWithReactions(
  supabase: SupabaseClient<Database>,
  commentIds: string[]
): Promise<Record<string, { clap_count: number; user_clap_count: number }>> {
  if (commentIds.length === 0) {
    return {};
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();

  // Get all reactions for these comments
  const { data: reactions, error } = await supabase
    .from("comment_reactions")
    .select("comment_id, user_id, reaction_type")
    .in("comment_id", commentIds)
    .eq("reaction_type", "clap");

  if (error) {
    logException(error, {
      extra: { commentIds },
    });
    return {};
  }

  // Count reactions per comment and track user's reactions
  const clapCounts: Record<string, number> = {};
  const userClapCounts: Record<string, number> = {};

  reactions?.forEach((reaction) => {
    clapCounts[reaction.comment_id] =
      (clapCounts[reaction.comment_id] || 0) + 1;

    if (session?.user && reaction.user_id === session.user.id) {
      userClapCounts[reaction.comment_id] =
        (userClapCounts[reaction.comment_id] || 0) + 1;
    }
  });

  // Build result object
  const result: Record<
    string,
    { clap_count: number; user_clap_count: number }
  > = {};
  commentIds.forEach((id) => {
    result[id] = {
      clap_count: clapCounts[id] || 0,
      user_clap_count: userClapCounts[id] || 0,
    };
  });

  return result;
}

// Helper function to check if user has reached clap limit
export const MAX_COMMENT_CLAP_REACTIONS = 20; // Same as video claps

export async function canUserClapComment(
  supabase: SupabaseClient<Database>,
  commentId: string,
  type: CommentReaction["reaction_type"] = "clap"
): Promise<boolean> {
  const userClapCount = await getUserReactionCountForComment(
    supabase,
    commentId,
    type
  );
  return userClapCount < MAX_COMMENT_CLAP_REACTIONS;
}
